import { c as _c } from "react/compiler-runtime";
import { useLazyGetAuthJwtQuery } from '@repo/store/maps/api/mapsApi';
import { useCallback, useEffect, useRef, useState } from 'react';
const WORKER_SERVICE = 'cloudflare-worker';
const JWT_REFRESH_BUFFER_SECONDS = 60;
const UNAUTHORIZED_COOLDOWN_MS = 30_000;
function extractJwtExpiration(token: string): number | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return null;
    }
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(payload.length + (4 - payload.length % 4) % 4, '=');
    const decodedPayload = JSON.parse(atob(base64)) as {
      exp?: unknown;
    };
    return typeof decodedPayload.exp === 'number' ? decodedPayload.exp : null;
  } catch {
    return null;
  }
}
export function useAiStreamAuth(enabled) {
  const $ = _c(10);
  const [fetchAuthJwt] = useLazyGetAuthJwtQuery();
  const [authToken, setAuthToken] = useState(null);
  const authJwtRef = useRef(null);
  const unauthorizedCooldownUntilRef = useRef(null);
  let t0;
  if ($[0] !== fetchAuthJwt) {
    t0 = async t1 => {
      const forceRefresh = t1 === undefined ? false : t1;
      if (forceRefresh && unauthorizedCooldownUntilRef.current) {
        const remainingMs = unauthorizedCooldownUntilRef.current - Date.now();
        if (remainingMs > 0) {
          throw new Error(`Suggestion auth temporarily unavailable. Please wait ${Math.ceil(remainingMs / 1000)} seconds and try again.`);
        }
        unauthorizedCooldownUntilRef.current = null;
      }
      if (!forceRefresh && authJwtRef.current) {
        const exp = extractJwtExpiration(authJwtRef.current);
        const now = Date.now() / 1000;
        if (exp === null || exp - now > JWT_REFRESH_BUFFER_SECONDS) {
          return authJwtRef.current;
        }
      }
      const jwt = await fetchAuthJwt(WORKER_SERVICE).unwrap();
      authJwtRef.current = jwt;
      setAuthToken(jwt);
      return jwt;
    };
    $[0] = fetchAuthJwt;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const refreshAuthToken = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      unauthorizedCooldownUntilRef.current = Date.now() + UNAUTHORIZED_COOLDOWN_MS;
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const notifyUnauthorized = t1;
  let t2;
  let t3;
  if ($[3] !== enabled || $[4] !== refreshAuthToken) {
    t2 = () => {
      if (!enabled) {
        authJwtRef.current = null;
        unauthorizedCooldownUntilRef.current = null;
        setAuthToken(null);
        return;
      }
      refreshAuthToken();
    };
    t3 = [enabled, refreshAuthToken];
    $[3] = enabled;
    $[4] = refreshAuthToken;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== authToken || $[8] !== refreshAuthToken) {
    t4 = {
      authToken,
      refreshAuthToken,
      notifyUnauthorized
    };
    $[7] = authToken;
    $[8] = refreshAuthToken;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}